/**
 * Google Analytics 4 — chargé uniquement si activé en base (back-office) et si
 * l'utilisateur accepte les cookies « analytics ».
 */

const MEASUREMENT_ID_RE = /^G-[A-Z0-9]+$/i;

function normalizeMeasurementId(id) {
  return String(id || '').trim().toUpperCase();
}

const defaultRuntimeConfig = { enabled: false, measurement_id: '' };

let runtimeConfig = { ...defaultRuntimeConfig };
let gaLoaded = false;
let activeMeasurementId = '';

export function setGoogleAnalyticsRuntimeConfig(cfg) {
  const enabled = Boolean(cfg?.enabled);
  const measurement_id = normalizeMeasurementId(cfg?.measurement_id);
  runtimeConfig =
    enabled && MEASUREMENT_ID_RE.test(measurement_id)
      ? { enabled: true, measurement_id }
      : { ...defaultRuntimeConfig };
}

export function getGoogleAnalyticsRuntimeConfig() {
  return { ...runtimeConfig };
}

export function isGoogleAnalyticsConfigured() {
  return Boolean(runtimeConfig.enabled && runtimeConfig.measurement_id);
}

export function getGoogleAnalyticsSiteDomain() {
  if (typeof window === 'undefined') return 'wownautic.com';
  return window.location.hostname || 'wownautic.com';
}

export function getGoogleAnalyticsCookieTableBody(domain = getGoogleAnalyticsSiteDomain()) {
  return [
    {
      name: '_ga',
      domain,
      description: 'Google Analytics — distinguer les utilisateurs',
      expiration: '2 ans',
    },
    {
      name: '_ga_*',
      domain,
      description: 'Google Analytics — état de session GA4',
      expiration: '2 ans',
    },
    {
      name: '_gid',
      domain,
      description: 'Google Analytics — distinguer les utilisateurs',
      expiration: '24 h',
    },
  ];
}

function ensureGtagStub() {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

export function isGoogleAnalyticsActive() {
  return gaLoaded && isGoogleAnalyticsConfigured() && typeof window.gtag === 'function';
}

export function initGoogleAnalytics() {
  if (typeof window === 'undefined' || gaLoaded || !isGoogleAnalyticsConfigured()) return false;

  const measurementId = runtimeConfig.measurement_id;
  activeMeasurementId = measurementId;
  delete window[`ga-disable-${measurementId}`];

  ensureGtagStub();
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
    anonymize_ip: true,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  script.setAttribute('data-ga', 'wownautic');
  document.head.appendChild(script);

  gaLoaded = true;
  return true;
}

export function disableGoogleAnalytics() {
  if (typeof window === 'undefined') return;
  if (activeMeasurementId) {
    window[`ga-disable-${activeMeasurementId}`] = true;
  }
  document.querySelectorAll('script[data-ga="wownautic"]').forEach((el) => el.remove());
  gaLoaded = false;
}

export function syncGoogleAnalyticsWithConsent(acceptedAnalytics) {
  if (!isGoogleAnalyticsConfigured()) return;
  if (acceptedAnalytics) {
    initGoogleAnalytics();
    trackGoogleAnalyticsPageView();
  } else {
    disableGoogleAnalytics();
  }
}

export function trackGoogleAnalyticsPageView(pathname, documentTitle) {
  if (!isGoogleAnalyticsActive()) return;
  const path = pathname || window.location.pathname + window.location.search;
  const title = documentTitle || document.title;
  window.gtag('config', runtimeConfig.measurement_id, {
    page_path: path,
    page_title: title,
    page_location: `${window.location.origin}${path}`,
  });
}
